import { PageHero } from "@/components/PageHero";
import { Section } from "@/components/Section";
import { useSeo } from "@/lib/seo";
import {
  Anchor,
  Box,
  Divider,
  Grid,
  Stack,
  Text,
  Title,
} from "@repo/ui/core";
import { Link } from "react-router";

const LAST_UPDATED = "March 22, 2026";
const EFFECTIVE_DATE = "March 22, 2026";
const CONTACT_EMAIL = "legal@null.autos";
const COMPANY_NAME = "Null Autos";
const COMPANY_LEGAL = "Null Autos Inc";

const sections = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "scope", label: "Scope of These Terms" },
  { id: "intellectual-property", label: "Intellectual Property" },
  { id: "acceptable-use", label: "Acceptable Use" },
  { id: "third-party", label: "Third-Party Links" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact", label: "Contact Us" },
];

function PolicySection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box component="section" id={id} pt="xs">
      <Title order={2} size="1.375rem" fw={700} mb="md">
        {title}
      </Title>
      <Stack gap="md">{children}</Stack>
      <Divider mt="xl" color="dark.7" />
    </Box>
  );
}

function PolicyParagraph({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" c="dimmed" lh={1.8}>
      {children}
    </Text>
  );
}

function PolicySubhead({ children }: { children: React.ReactNode }) {
  return (
    <Text fw={600} size="sm" mt="xs">
      {children}
    </Text>
  );
}

export default function Terms() {
  useSeo("/terms");

  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Terms of Use"
        description="The terms and conditions that govern your use of the null.autos marketing website."
      >
        <Text size="sm" c="dimmed">
          Effective: {EFFECTIVE_DATE} &nbsp;|&nbsp; Last updated: {LAST_UPDATED}
        </Text>
      </PageHero>

      <Section>
        <Grid gutter={60}>
          {/* Table of contents - sticky sidebar on desktop */}
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Box
              style={{
                position: "sticky",
                top: "calc(var(--na-nav-h) + 24px)",
              }}
            >
              <Text fw={600} size="sm" mb="md" tt="uppercase" lts="0.06em" c="dimmed">
                Contents
              </Text>
              <Stack gap={6}>
                {sections.map((s) => (
                  <Anchor
                    key={s.id}
                    href={`#${s.id}`}
                    size="sm"
                    c="dimmed"
                    underline="never"
                    style={{ transition: "color 150ms ease" }}
                  >
                    {s.label}
                  </Anchor>
                ))}
              </Stack>
              <Divider my="xl" color="dark.7" />
              <Text size="xs" c="dimmed" lh={1.7}>
                Legal questions?{" "}
                <Anchor href={`mailto:${CONTACT_EMAIL}`} size="xs">
                  {CONTACT_EMAIL}
                </Anchor>
              </Text>
            </Box>
          </Grid.Col>

          {/* Terms content */}
          <Grid.Col span={{ base: 12, md: 9 }}>
            <Stack gap="xl">
              {/* Intro */}
              <Box>
                <PolicyParagraph>
                  These Terms of Use (&ldquo;Terms&rdquo;) govern your access to and use of the {COMPANY_NAME} marketing website located at null.autos (the &ldquo;Website&rdquo;), operated by {COMPANY_LEGAL} (&ldquo;{COMPANY_NAME},&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
                </PolicyParagraph>
                <PolicyParagraph>
                  By visiting or using this Website, you agree to be bound by these Terms and our{" "}
                  <Anchor component={Link} to="/privacy" size="sm">
                    Privacy Policy
                  </Anchor>
                  . If you do not agree, please discontinue use of this Website.
                </PolicyParagraph>
                <Divider mt="xl" color="dark.7" />
              </Box>

              {/* 1. Acceptance */}
              <PolicySection id="acceptance" title="1. Acceptance of Terms">
                <PolicyParagraph>
                  By accessing this Website, you confirm that: (a) you are at least 18 years old or are accessing the Website under the supervision of a parent or guardian; (b) you have the legal capacity to agree to these Terms; and (c) you agree to comply with these Terms in full.
                </PolicyParagraph>
                <PolicyParagraph>
                  We reserve the right to modify these Terms at any time. Continued use of the Website after changes are posted constitutes your acceptance of the revised Terms.
                </PolicyParagraph>
              </PolicySection>

              {/* 2. Scope */}
              <PolicySection id="scope" title="2. Scope of These Terms">
                <PolicyParagraph>
                  These Terms apply <strong>solely</strong> to your use of the null.autos marketing website, including its pages, content, contact forms, and demo scheduling features. They do not govern your use of any {COMPANY_NAME} software products, developer tools, APIs, CI/CD platform, emulation infrastructure, or any other services offered by {COMPANY_LEGAL}.
                </PolicyParagraph>
                <PolicyParagraph>
                  Use of {COMPANY_NAME} software products and services is governed exclusively by separate product-specific license agreements, subscription terms, master service agreements (MSAs), and data processing agreements entered into directly with {COMPANY_LEGAL}. If you have questions about the terms applicable to a specific product, please contact{" "}
                  <Anchor href={`mailto:${CONTACT_EMAIL}`} size="sm">
                    {CONTACT_EMAIL}
                  </Anchor>
                  .
                </PolicyParagraph>
                <PolicyParagraph>
                  The Website serves as a marketing and informational resource only. Nothing on the Website constitutes a binding offer, commitment to provide services, or warranty regarding the capabilities or availability of any {COMPANY_NAME} product.
                </PolicyParagraph>
              </PolicySection>

              {/* 3. IP */}
              <PolicySection id="intellectual-property" title="3. Intellectual Property">
                <PolicySubhead>Website Content</PolicySubhead>
                <PolicyParagraph>
                  All content on this Website, including text, graphics, logos, icons, images, design elements, and layout, is owned by or licensed to {COMPANY_LEGAL} and is protected by applicable copyright, trademark, and intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any Website content without our prior written permission.
                </PolicyParagraph>
                <PolicySubhead>Trademarks</PolicySubhead>
                <PolicyParagraph>
                  &ldquo;Null Autos,&rdquo; the {COMPANY_NAME} logo, and all related marks, names, and trade dress are trademarks of {COMPANY_LEGAL}. Nothing on this Website grants any licence or right to use these marks without our express written consent. All other trademarks appearing on this Website belong to their respective owners.
                </PolicyParagraph>
                <PolicySubhead>Limited Personal Use</PolicySubhead>
                <PolicyParagraph>
                  You may view and print pages from this Website for your own personal, non-commercial reference. This permission does not include the right to republish, commercialise, or systematically download Website content.
                </PolicyParagraph>
              </PolicySection>

              {/* 4. Acceptable Use */}
              <PolicySection id="acceptable-use" title="4. Acceptable Use">
                <PolicyParagraph>
                  When using this Website you must not:
                </PolicyParagraph>
                <Stack gap={6} pl="md">
                  {[
                    "Use the Website for any unlawful purpose or in violation of any applicable law or regulation",
                    "Attempt to gain unauthorised access to any part of the Website or its underlying infrastructure",
                    "Transmit any malware, viruses, or other malicious code via the Website",
                    "Use automated tools (scrapers, bots, crawlers) to extract content in bulk or at a rate that places undue load on our servers",
                    "Misrepresent your identity or affiliation when submitting contact forms or demo requests",
                    "Post or transmit any content that is defamatory, harassing, obscene, or otherwise objectionable",
                    "Interfere with or disrupt the integrity or performance of the Website",
                  ].map((item) => (
                    <Text key={item} size="sm" c="dimmed" lh={1.7}>
                      &bull;&nbsp;&nbsp;{item}
                    </Text>
                  ))}
                </Stack>
                <PolicyParagraph>
                  We reserve the right to block access to the Website for any visitor who violates these standards.
                </PolicyParagraph>
              </PolicySection>

              {/* 5. Third-Party Links */}
              <PolicySection id="third-party" title="5. Third-Party Links">
                <PolicyParagraph>
                  This Website may contain links to third-party websites, including GitLab, GitHub, social media platforms, and cloud provider documentation. These links are provided for convenience only. {COMPANY_NAME} does not endorse, control, or assume responsibility for the content, privacy practices, or accuracy of any third-party website. Your use of third-party websites is at your own risk and subject to their respective terms and policies.
                </PolicyParagraph>
              </PolicySection>

              {/* 6. Disclaimers */}
              <PolicySection id="disclaimers" title="6. Disclaimers">
                <PolicyParagraph>
                  THIS WEBSITE AND ALL CONTENT ON IT ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT ANY WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY_NAME.toUpperCase()} DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </PolicyParagraph>
                <PolicyParagraph>
                  Website content is provided for general informational purposes only. Product descriptions, feature lists, and technical specifications on this Website are subject to change and may not reflect the current state of any {COMPANY_NAME} product. They do not constitute a warranty or representation of any kind. Refer to your product agreement or contact our team for authoritative product information.
                </PolicyParagraph>
                <PolicyParagraph>
                  Some jurisdictions do not allow the exclusion of implied warranties, so the above exclusion may not apply to you in full.
                </PolicyParagraph>
              </PolicySection>

              {/* 7. Liability */}
              <PolicySection id="liability" title="7. Limitation of Liability">
                <PolicyParagraph>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY_NAME.toUpperCase()} AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF, OR INABILITY TO USE, THIS WEBSITE OR ITS CONTENT.
                </PolicyParagraph>
                <PolicyParagraph>
                  Our total aggregate liability to you in connection with this Website will not exceed one hundred US dollars (USD $100). These limitations apply regardless of the theory of liability. Some jurisdictions do not allow certain limitations, so the above may not fully apply to you.
                </PolicyParagraph>
              </PolicySection>

              {/* 8. Governing Law */}
              <PolicySection id="governing-law" title="8. Governing Law">
                <PolicyParagraph>
                  These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles. Any dispute arising from these Terms or your use of this Website that cannot be resolved informally shall be subject to the exclusive jurisdiction of the courts of the State of Delaware.
                </PolicyParagraph>
                <PolicyParagraph>
                  If you are an EU or UK resident, mandatory consumer protection laws of your country of residence shall apply in addition to the above.
                </PolicyParagraph>
                <PolicyParagraph>
                  Before initiating any formal proceedings, you agree to contact us at{" "}
                  <Anchor href={`mailto:${CONTACT_EMAIL}`} size="sm">
                    {CONTACT_EMAIL}
                  </Anchor>{" "}
                  with a brief written description of the dispute. Both parties agree to attempt in good faith to resolve the matter within 30 days.
                </PolicyParagraph>
              </PolicySection>

              {/* 9. Changes */}
              <PolicySection id="changes" title="9. Changes to These Terms">
                <PolicyParagraph>
                  We may modify these Terms at any time by posting the revised version on this page with an updated &ldquo;Last updated&rdquo; date. Material changes will be highlighted with a notice on the Website. Your continued use of the Website after the effective date of any revision constitutes your acceptance of the updated Terms. If you disagree with the changes, please stop using the Website.
                </PolicyParagraph>
              </PolicySection>

              {/* 10. Contact */}
              <Box component="section" id="contact" pt="xs">
                <Title order={2} size="1.375rem" fw={700} mb="md">
                  10. Contact Us
                </Title>
                <Stack gap="md">
                  <PolicyParagraph>
                    For questions or notices regarding these Terms of Use, please contact:
                  </PolicyParagraph>
                  <Stack gap={6} pl="md">
                    <Text size="sm" c="dimmed" lh={1.7}>
                      <strong>Email:</strong>{" "}
                      <Anchor href={`mailto:${CONTACT_EMAIL}`} size="sm">
                        {CONTACT_EMAIL}
                      </Anchor>
                    </Text>
                    <Text size="sm" c="dimmed" lh={1.7}>
                      <strong>Company:</strong> {COMPANY_LEGAL}
                    </Text>
                  </Stack>
                  <Text size="sm" c="dimmed" lh={1.7}>
                    View our{" "}
                    <Anchor component={Link} to="/privacy" size="sm">
                      Privacy Policy
                    </Anchor>{" "}
                    to understand how we handle your data when you visit this Website.
                  </Text>
                </Stack>
              </Box>
            </Stack>
          </Grid.Col>
        </Grid>
      </Section>
    </>
  );
}
